import { request } from '@umijs/max';

export type AssetFileType = 'VIDEO' | 'IMAGE' | 'AUDIO' | 'BLOG';
export type AssetSourceType = 'SYSTEM_GENERATED' | 'USER_UPLOAD';
export type AssetItem = API.AssetItem;
export type AssetDataList = API.AssetDataList;

type AssetApiResponse<T> = {
  code?: number;
  msg?: string;
  data?: T;
};

export type AssetRegistrationItem = {
  sourceUrl: string;
  status: 'SUCCESS' | 'PROCESSING' | 'FAILED';
  errorMsg?: string | null;
};

type AssetRegistrationBatchData = {
  batchId?: string;
  allReady?: boolean;
  items?: AssetRegistrationItem[];
};

export async function createAssetRegistrationBatch(
  items: string[],
  options?: { [key: string]: any }
) {
  return request<AssetApiResponse<AssetRegistrationBatchData>>('/asset/v1/byteplus/asset-registration-batches', {
    method: 'POST',
    data: { storageProvider: 'GCS', items },
    ...(options || {}),
  });
}

export async function getAssetRegistrationBatch(
  batchId: string,
  options?: { [key: string]: any }
) {
  return request<AssetApiResponse<AssetRegistrationBatchData>>(`/asset/v1/byteplus/asset-registration-batches/${batchId}`, {
    method: 'GET',
    ...(options || {}),
  });
}

// 获取资产列表
export async function getAssetList(
  params: {
    sessionId?: string;
    fileType?: Array<AssetFileType>;
    sourceType?: Array<AssetSourceType>;
    startTime?: string; // yyyy-MM-dd
    endTime?: string; // yyyy-MM-dd
    offset?: string; // 分页偏移量
    size?: number; // 分页大小 后端默认50
  },
  options?: { [key: string]: any },
) {
  return request<API.ApiResponse>(`/asset/v1/page`, {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

// 保存会话配置
export async function sessionConfigSave(
  data: {
    sessionId: string;
    url?: string;
    step?: number;
    brandId?: string;
    skillIds?: string[];
    generationParams?: object | null;
    modelSelect?: string[];
    canvasId?: string;
    entityCount?: number;
  },
  options?: { [key: string]: any },
) {
  return request<API.ApiResponse>(`/asset/v1/session_config/save`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

// 查询会话配置
export async function sessionConfigQuery(
  data: {
    sessionId: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.ApiResponse>(`/asset/v1/session_config/query`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

// 获取会话历史消息
export async function sessionHistoryMessages(
  data: {
    sessionId: string;
    agent_family: string;
    agent?: string;
    limit?: number;
    asset_message_id?: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.ApiResponse>(`/agent-api/v1/agent/conversations/${data.sessionId}/messages`, {
    method: 'GET',
    params: {
      limit: data.limit,
      agent_family: data.agent_family,
      agent: data.agent,
      asset_message_id: data.asset_message_id,
    },
    ...(options || {}),
  });
}

// 获取会话历史消息
export async function sessionHistoryRead(data: { sessionId: string; latest_read_stream_id: string }, options?: { [key: string]: any }) {
  return request<API.ApiResponse>(`/agent-api/v1/agent/conversations/${data.sessionId}/read`, {
    data: {
      latest_read_stream_id: data.latest_read_stream_id,
    },
    method: 'POST',
    ...(options || {}),
  });
}

export async function getAgentConversationNav(params: API.AgentConversationListParams = {}, options?: { [key: string]: any }) {
  return request<AssetApiResponse<API.AgentConversationListData>>(`/agent-api/v1/agent/conversations/nav`, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function getAgentConversationFacets(options?: { [key: string]: any }) {
  return request<AssetApiResponse<API.AgentConversationFacetsData>>(`/agent-api/v1/agent/conversations/facets`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getAgentConversationList(params: API.AgentConversationListParams = {}, options?: { [key: string]: any }) {
  return request<AssetApiResponse<API.AgentConversationListData>>(`/agent-api/v1/agent/conversations`, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function deleteAgentConversation(sessionId: string, options?: { [key: string]: any }) {
  return request<AssetApiResponse<string>>(`/agent-api/v1/agent/conversations/${sessionId}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function updateAgentConversationTitle(sessionId: string, data: API.AgentConversationTitlePayload, options?: { [key: string]: any }) {
  return request<AssetApiResponse<string | API.AgentConversationItem>>(`/agent-api/v1/agent/conversations/${sessionId}/title`, {
    method: 'PUT',
    data,
    ...(options || {}),
  });
}

export async function getAgentConversationStatus(data: API.AgentConversationStatusPayload, options?: { [key: string]: any }) {
  return request<AssetApiResponse<API.AgentConversationStatusData>>(`/agent-api/v1/agent/conversations/status`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}
